import { Card, CardBody, CardHeader, Chip, ChipProps, Spinner, Switch } from '@nextui-org/react'
import React, { useCallback, useEffect, useState } from 'react'
import { Button } from '~/components/common/Button'
import { Input } from '~/components/common/Input'
import { Modal, ModalBody, ModalFooter } from '~/components/modals/CurieModal'
import { Pagination } from '~/components/common/Pagination'
import {
  executeScript,
  listScripts,
  ScriptAction,
  ScriptExecuteResponse,
  ScriptInfo
} from '~/services/superAdminService'
import { BetterToast } from '~/utils/toast'

// Status color mapping
const statusColorMap: Record<string, ChipProps['color']> = {
  success: 'success',
  partial: 'warning',
  failed: 'danger',
  dry_run: 'primary'
}

// Action type color mapping
const actionColorMap: Record<string, ChipProps['color']> = {
  create: 'success',
  update: 'primary',
  skip: 'default',
  error: 'danger'
}

interface ScriptParamsFormProps {
  schema: Record<string, unknown> | null
  params: Record<string, unknown>
  onChange: (params: Record<string, unknown>) => void
}

// Dynamic form for script parameters based on schema
const ScriptParamsForm: React.FC<ScriptParamsFormProps> = ({ schema, params, onChange }) => {
  if (!schema || !schema.properties) {
    return <p className='text-sm text-gray-500'>This script has no configurable parameters.</p>
  }

  const properties = schema.properties as Record<
    string,
    {
      type: string
      description?: string
      default?: unknown
      enum?: string[]
    }
  >

  const handleChange = (key: string, value: unknown) => {
    onChange({ ...params, [key]: value })
  }

  return (
    <div className='space-y-4'>
      {Object.entries(properties).map(([key, prop]) => (
        <div key={key} className='flex flex-col gap-1'>
          <label className='text-sm font-medium text-gray-700'>
            {key}
            {prop.default !== undefined && (
              <span className='text-gray-400 ml-2'>(default: {String(prop.default)})</span>
            )}
          </label>
          {prop.type === 'boolean' ? (
            <Switch
              isSelected={params[key] as boolean}
              onValueChange={v => handleChange(key, v)}
              size='sm'
            >
              {params[key] ? 'Enabled' : 'Disabled'}
            </Switch>
          ) : prop.enum ? (
            <select
              className='border rounded-md p-2 text-sm'
              value={String(params[key] ?? prop.default ?? '')}
              onChange={e => handleChange(key, e.target.value)}
            >
              {prop.enum.map(opt => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
          ) : prop.type === 'integer' ? (
            <Input
              type='text'
              inputMode='numeric'
              value={String(params[key] ?? '')}
              onChange={e => {
                const raw = e.target.value.replace(/[^0-9-]/g, '')
                const parsed = parseInt(raw, 10)
                handleChange(key, raw === '' || Number.isNaN(parsed) ? undefined : parsed)
              }}
              placeholder={prop.default !== undefined ? String(prop.default) : ''}
            />
          ) : (
            <Input
              type='text'
              value={String(params[key] ?? '')}
              onChange={e => handleChange(key, e.target.value || undefined)}
              placeholder={prop.default !== undefined ? String(prop.default) : ''}
            />
          )}
          {prop.description && <p className='text-xs text-gray-500'>{prop.description}</p>}
        </div>
      ))}
    </div>
  )
}

// ---------- Flow Report types ----------
interface FlowReportFlow {
  flow_name: string
  flow_type: string
  repo_name: string
  org_id: number | null
  workspace_name: string
  date: string | null
  task_ids: number[]
  kept_task_id: number | null
  is_duplicate: boolean
}

interface RawFlowEntry {
  flow_name: string
  flow_type: string
  repo_name: string
  org_id: number | null
  workspace_name: string
  task_id: number | null
  date: string | null
}

interface FlowReportTaskBreakdown {
  task_id: number
  flow_count: number
  flows: string[]
}

interface FlowReportData {
  billing_info: Record<string, unknown>
  date_range: { start: string; end: string }
  flows: FlowReportFlow[]
  raw_flows: RawFlowEntry[]
  task_breakdown: FlowReportTaskBreakdown[]
  stats: {
    raw_flow_entries: number
    unique_flows: number
    duplicates_removed: number
    total_tasks: number
    duplicate_flows: number
  }
}

// ---------- Custom billable flow report renderer ----------
const BillableFlowReportResults: React.FC<{
  result: ScriptExecuteResponse
  reportData: FlowReportData
}> = ({ result, reportData }) => {
  const [activeTab, setActiveTab] = useState<'raw' | 'effective' | 'deduped'>('raw')
  const [showTaskBreakdown, setShowTaskBreakdown] = useState(false)
  const [rawPage, setRawPage] = useState(1)
  const [effectivePage, setEffectivePage] = useState(1)
  const [dedupedPage, setDedupedPage] = useState(1)
  const PAGE_SIZE = 8
  const { billing_info, date_range, flows, raw_flows, task_breakdown, stats } = reportData

  const duplicateFlows = flows.filter(f => f.is_duplicate)

  const currentPage =
    activeTab === 'raw' ? rawPage : activeTab === 'effective' ? effectivePage : dedupedPage
  const setCurrentPage =
    activeTab === 'raw' ? setRawPage : activeTab === 'effective' ? setEffectivePage : setDedupedPage
  const currentList =
    activeTab === 'raw' ? raw_flows : activeTab === 'effective' ? flows : duplicateFlows
  const totalPages = Math.max(1, Math.ceil(currentList.length / PAGE_SIZE))
  const pageStart = (currentPage - 1) * PAGE_SIZE
  const pagedRawFlows = raw_flows.slice(pageStart, pageStart + PAGE_SIZE)
  const pagedFlows = flows.slice(pageStart, pageStart + PAGE_SIZE)
  const pagedDuplicateFlows = duplicateFlows.slice(pageStart, pageStart + PAGE_SIZE)

  const formatDate = (iso: string | null) => {
    if (!iso) return '—'
    try {
      return new Date(iso).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      })
    } catch {
      return iso
    }
  }

  const extractRepoShort = (repo: string) => {
    if (!repo) return ''
    const parts = repo.replace(/\.git$/, '').split('/')
    return parts[parts.length - 1] || repo
  }

  const billingLabel = billing_info.billing_email
    ? String(billing_info.billing_email)
    : billing_info.name
      ? String(billing_info.name)
      : billing_info.billing_id
        ? `Billing #${String(billing_info.billing_id)}`
        : 'Unknown'

  const renderFlowRow = (flow: FlowReportFlow, idx: number, showDedupColumns: boolean) => {
    const dedupedIds = flow.task_ids.filter(id => id !== flow.kept_task_id)
    const rowBg = showDedupColumns ? 'bg-amber-50 hover:bg-amber-100' : 'hover:bg-gray-50'

    return (
      <tr key={idx} className={`${rowBg} border-t border-gray-200 transition-colors`}>
        <td className='px-3 py-2'>
          <div className='font-medium text-gray-900 max-w-[220px] truncate' title={flow.flow_name}>
            {flow.flow_name}
          </div>
        </td>
        <td className='px-3 py-2 text-gray-600 max-w-[160px] overflow-hidden'>
          <div className='truncate' title={flow.workspace_name || undefined}>
            {flow.workspace_name || '—'}
          </div>
          {flow.org_id != null && (
            <div className='text-[10px] text-gray-400'>org: {flow.org_id}</div>
          )}
        </td>
        <td className='px-3 py-2 text-gray-600 max-w-[160px] overflow-hidden'>
          <div className='truncate' title={flow.repo_name || undefined}>
            {flow.repo_name ? extractRepoShort(flow.repo_name) : '—'}
          </div>
        </td>
        <td className='px-3 py-2'>
          <Chip
            size='sm'
            variant='flat'
            color={
              flow.flow_type === 'CODE_MIGRATION'
                ? 'secondary'
                : flow.flow_type === 'INTEGRATION_TEST'
                  ? 'primary'
                  : 'default'
            }
          >
            {flow.flow_type}
          </Chip>
        </td>
        <td className='px-3 py-2 text-gray-600 whitespace-nowrap'>{formatDate(flow.date)}</td>
        {showDedupColumns ? (
          <>
            <td className='px-3 py-2'>
              {dedupedIds.length > 0 ? (
                <span className='text-gray-400 line-through'>{dedupedIds.join(', ')}</span>
              ) : (
                <span className='text-gray-400'>—</span>
              )}
            </td>
            <td className='px-3 py-2'>
              {flow.kept_task_id != null ? (
                <span className='font-bold text-purple-700'>{flow.kept_task_id}</span>
              ) : (
                <span className='text-gray-400'>—</span>
              )}
            </td>
          </>
        ) : (
          <td className='px-3 py-2'>
            <span className='text-gray-600'>{flow.task_ids.join(', ') || '—'}</span>
          </td>
        )}
      </tr>
    )
  }

  return (
    <div className='space-y-4'>
      {/* Compact header */}
      <div className='flex items-center justify-between'>
        <div className='flex items-center gap-2'>
          <Chip color={statusColorMap[result.status]} variant='flat' size='sm'>
            {result.status.toUpperCase()}
          </Chip>
          <span className='text-sm font-semibold text-gray-900'>Billable Flow Report</span>
          <span className='text-sm text-gray-500'>—</span>
          <span className='text-sm text-gray-600'>{billingLabel}</span>
        </div>
        <div className='text-xs text-gray-400'>
          {formatDate(date_range.start)} – {formatDate(date_range.end)}
          {result.duration_seconds != null && (
            <span className='ml-2'>({result.duration_seconds.toFixed(2)}s)</span>
          )}
        </div>
      </div>

      {/* Stats strip */}
      <div className='grid grid-cols-3 sm:grid-cols-5 gap-1.5'>
        {[
          {
            label: 'Raw Entries',
            value: stats.raw_flow_entries,
            color: 'text-gray-600',
            bg: 'bg-gray-50'
          },
          {
            label: 'Effective Flows',
            value: stats.unique_flows,
            color: 'text-emerald-700',
            bg: 'bg-emerald-50'
          },
          {
            label: 'Deduped',
            value: stats.duplicates_removed,
            color: 'text-amber-700',
            bg: 'bg-amber-50'
          },
          { label: 'Tasks', value: stats.total_tasks, color: 'text-gray-600', bg: 'bg-gray-50' }
        ].map(s => (
          <div key={s.label} className={`${s.bg} rounded-md px-2 py-1.5 text-center`}>
            <div className={`text-lg font-bold leading-tight ${s.color}`}>{s.value}</div>
            <div className='text-[10px] text-gray-500 leading-tight'>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Errors */}
      {result.errors.length > 0 && (
        <div className='bg-danger-50 rounded-lg p-3'>
          <h4 className='text-sm font-semibold text-danger mb-2'>
            Errors ({result.errors.length})
          </h4>
          <ul className='list-disc list-inside text-xs text-danger space-y-1'>
            {result.errors.map((e, i) => (
              <li key={i}>{e}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Tabs */}
      <div className='border-b border-gray-200'>
        <div className='flex gap-0'>
          <button
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'raw'
                ? 'border-gray-900 text-gray-900'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
            onClick={() => {
              setActiveTab('raw')
              setRawPage(1)
            }}
          >
            Raw Flows ({raw_flows.length})
          </button>
          <button
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'effective'
                ? 'border-emerald-600 text-emerald-700'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
            onClick={() => {
              setActiveTab('effective')
              setEffectivePage(1)
            }}
          >
            Effective Flows ({flows.length})
          </button>
          <button
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'deduped'
                ? 'border-amber-600 text-amber-700'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
            onClick={() => {
              setActiveTab('deduped')
              setDedupedPage(1)
            }}
          >
            Deduped Flows ({duplicateFlows.length})
          </button>
        </div>
      </div>

      {/* Flow table */}
      <div className='border rounded-lg overflow-hidden'>
        <div>
          {activeTab === 'raw' ? (
            <table className='w-full text-xs'>
              <thead className='bg-gray-100 sticky top-0 z-10'>
                <tr>
                  <th className='text-left px-3 py-2 font-semibold'>Flow Name</th>
                  <th className='text-left px-3 py-2 font-semibold'>Workspace</th>
                  <th className='text-left px-3 py-2 font-semibold'>Repo</th>
                  <th className='text-left px-3 py-2 font-semibold'>Type</th>
                  <th className='text-left px-3 py-2 font-semibold'>Date</th>
                  <th className='text-left px-3 py-2 font-semibold'>Task ID</th>
                </tr>
              </thead>
              <tbody>
                {pagedRawFlows.length > 0 ? (
                  pagedRawFlows.map((rf, idx) => (
                    <tr
                      key={idx}
                      className='hover:bg-gray-50 border-t border-gray-200 transition-colors'
                    >
                      <td className='px-3 py-2'>
                        <div
                          className='font-medium text-gray-900 max-w-[220px] truncate'
                          title={rf.flow_name}
                        >
                          {rf.flow_name}
                        </div>
                      </td>
                      <td className='px-3 py-2 text-gray-600 max-w-[160px] overflow-hidden'>
                        <div className='truncate' title={rf.workspace_name || undefined}>
                          {rf.workspace_name || '—'}
                        </div>
                        {rf.org_id != null && (
                          <div className='text-[10px] text-gray-400'>org: {rf.org_id}</div>
                        )}
                      </td>
                      <td className='px-3 py-2 text-gray-600 max-w-[160px] overflow-hidden'>
                        <div className='truncate' title={rf.repo_name || undefined}>
                          {rf.repo_name ? extractRepoShort(rf.repo_name) : '—'}
                        </div>
                      </td>
                      <td className='px-3 py-2'>
                        <Chip size='sm' variant='flat' color='default'>
                          {rf.flow_type}
                        </Chip>
                      </td>
                      <td className='px-3 py-2 text-gray-600 whitespace-nowrap'>
                        {formatDate(rf.date)}
                      </td>
                      <td className='px-3 py-2 text-gray-600'>{rf.task_id ?? '—'}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={7} className='text-center py-8 text-gray-500'>
                      No flows found for this period.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          ) : (
            <table className='w-full text-xs'>
              <thead className='bg-gray-100 sticky top-0 z-10'>
                <tr>
                  <th className='text-left px-3 py-2 font-semibold'>Flow Name</th>
                  <th className='text-left px-3 py-2 font-semibold'>Workspace</th>
                  <th className='text-left px-3 py-2 font-semibold'>Repo</th>
                  <th className='text-left px-3 py-2 font-semibold'>Type</th>
                  <th className='text-left px-3 py-2 font-semibold'>Date</th>
                  {activeTab === 'deduped' ? (
                    <>
                      <th className='text-left px-3 py-2 font-semibold'>Deduped Task IDs</th>
                      <th className='text-left px-3 py-2 font-semibold'>Kept Task</th>
                    </>
                  ) : (
                    <th className='text-left px-3 py-2 font-semibold'>Task IDs</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {activeTab === 'effective' ? (
                  pagedFlows.length > 0 ? (
                    pagedFlows.map((flow, idx) => renderFlowRow(flow, idx, false))
                  ) : (
                    <tr>
                      <td colSpan={7} className='text-center py-8 text-gray-500'>
                        No flows found for this period.
                      </td>
                    </tr>
                  )
                ) : pagedDuplicateFlows.length > 0 ? (
                  pagedDuplicateFlows.map((flow, idx) => renderFlowRow(flow, idx, true))
                ) : (
                  <tr>
                    <td colSpan={8} className='text-center py-8 text-gray-500'>
                      No duplicate flows found — all flows are unique.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {totalPages > 1 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
        />
      )}

      {/* Legend (only for deduped tab) */}
      {activeTab === 'deduped' && (
        <div className='flex items-center gap-4 text-[10px] text-gray-500'>
          <span className='flex items-center gap-1'>
            <span className='inline-block w-3 h-3 rounded bg-amber-100 border border-amber-300' />
            Duplicate flow
          </span>
          <span className='flex items-center gap-1'>
            <span className='line-through text-gray-400'>123</span> Deduped task ID
          </span>
          <span className='flex items-center gap-1'>
            <span className='font-bold text-purple-700'>456</span> Kept task ID
          </span>
        </div>
      )}

      {/* Task breakdown collapsible */}
      {task_breakdown.length > 0 && (
        <div className='border rounded-lg'>
          <button
            className='w-full flex items-center justify-between px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50'
            onClick={() => setShowTaskBreakdown(!showTaskBreakdown)}
          >
            <span>Per-Task Breakdown ({task_breakdown.length} tasks)</span>
            <span className='text-gray-400'>{showTaskBreakdown ? '▲' : '▼'}</span>
          </button>
          {showTaskBreakdown && (
            <div className='max-h-[200px] overflow-auto border-t'>
              <table className='w-full text-xs'>
                <thead className='bg-gray-50 sticky top-0'>
                  <tr>
                    <th className='text-left px-3 py-1 font-semibold'>Task ID</th>
                    <th className='text-left px-3 py-1 font-semibold'>Flows</th>
                    <th className='text-right px-3 py-1 font-semibold'>Count</th>
                  </tr>
                </thead>
                <tbody>
                  {task_breakdown.map(tb => (
                    <tr key={tb.task_id} className='border-t border-gray-100'>
                      <td className='px-3 py-1 font-mono text-gray-700'>{tb.task_id}</td>
                      <td className='px-3 py-1 text-gray-600 break-all'>{tb.flows.join(', ')}</td>
                      <td className='px-3 py-1 text-right text-gray-700'>{tb.flow_count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// ---------- Generic results display component ----------
interface ScriptResultsProps {
  result: ScriptExecuteResponse
}

const ScriptResults: React.FC<ScriptResultsProps> = ({ result }) => {
  const [showAllActions, setShowAllActions] = useState(false)
  const displayedActions = showAllActions ? result.actions : result.actions.slice(0, 10)

  // Check if this is a billable flow report with a flow_report_data action
  const flowReportAction = result.actions.find(a => a.action_type === 'flow_report_data')
  if (flowReportAction) {
    return (
      <BillableFlowReportResults
        result={result}
        reportData={flowReportAction.details as unknown as FlowReportData}
      />
    )
  }

  return (
    <div className='space-y-4'>
      {/* Status Banner */}
      <div
        className={`p-4 rounded-lg ${
          result.status === 'success'
            ? 'bg-success-50'
            : result.status === 'dry_run'
              ? 'bg-primary-50'
              : result.status === 'partial'
                ? 'bg-warning-50'
                : 'bg-danger-50'
        }`}
      >
        <div className='flex items-center gap-2'>
          <Chip color={statusColorMap[result.status]} variant='flat' size='sm'>
            {result.status.toUpperCase()}
          </Chip>
          <span className='text-sm font-medium'>{result.message}</span>
        </div>
        {result.duration_seconds && (
          <p className='text-xs text-gray-500 mt-1'>
            Completed in {result.duration_seconds.toFixed(2)} seconds
          </p>
        )}
      </div>

      {/* Summary */}
      <div className='grid grid-cols-4 gap-2'>
        {Object.entries(result.summary).map(([key, value]) => (
          <div key={key} className='bg-gray-50 rounded-lg p-3 text-center'>
            <div className='text-2xl font-bold'>{value}</div>
            <div className='text-xs text-gray-500 capitalize'>{key}</div>
          </div>
        ))}
      </div>

      {/* Errors */}
      {result.errors.length > 0 && (
        <div className='bg-danger-50 rounded-lg p-3'>
          <h4 className='text-sm font-semibold text-danger mb-2'>
            Errors ({result.errors.length})
          </h4>
          <ul className='list-disc list-inside text-xs text-danger space-y-1'>
            {result.errors.map((error, i) => (
              <li key={i}>{error}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Actions */}
      {result.actions.length > 0 && (
        <div>
          <h4 className='text-sm font-semibold mb-2'>
            Actions ({result.actions.length})
            {result.actions.length > 10 && !showAllActions && (
              <button
                className='ml-2 text-xs text-primary underline'
                onClick={() => setShowAllActions(true)}
              >
                Show all
              </button>
            )}
          </h4>
          <div className='space-y-2 max-h-[300px] overflow-auto'>
            {displayedActions.map((action, i) => (
              <ActionItem key={i} action={action} />
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

// Single action item display
const ActionItem: React.FC<{ action: ScriptAction }> = ({ action }) => {
  const [expanded, setExpanded] = useState(false)

  return (
    <div className='bg-gray-50 rounded-lg p-2 text-sm'>
      <div className='flex items-start gap-2'>
        <Chip
          color={actionColorMap[action.action_type] || 'default'}
          variant='flat'
          size='sm'
          className='shrink-0'
        >
          {action.action_type}
        </Chip>
        <div className='flex-1 min-w-0'>
          <p className='font-mono text-xs text-gray-600 truncate'>{action.target}</p>
          <p className='text-xs'>{action.description}</p>
        </div>
        {Object.keys(action.details).length > 0 && (
          <button
            className='text-xs text-primary underline shrink-0'
            onClick={() => setExpanded(!expanded)}
          >
            {expanded ? 'Hide' : 'Details'}
          </button>
        )}
      </div>
      {expanded && Object.keys(action.details).length > 0 && (
        <pre className='mt-2 text-xs bg-gray-100 p-2 rounded overflow-auto max-h-[100px]'>
          {JSON.stringify(action.details, null, 2)}
        </pre>
      )}
    </div>
  )
}

// Main ScriptRunner component
const ScriptRunner: React.FC = () => {
  const [scripts, setScripts] = useState<ScriptInfo[]>([])
  const [selectedScript, setSelectedScript] = useState<ScriptInfo | null>(null)
  const [params, setParams] = useState<Record<string, unknown>>({})
  const [dryRun, setDryRun] = useState(true)
  const [isLoading, setIsLoading] = useState(false)
  const [isExecuting, setIsExecuting] = useState(false)
  const [result, setResult] = useState<ScriptExecuteResponse | null>(null)
  const [showResultModal, setShowResultModal] = useState(false)

  // Fetch available scripts
  const fetchScripts = useCallback(async () => {
    try {
      setIsLoading(true)
      const response = await listScripts()
      setScripts(response.scripts)
    } catch (error) {
      BetterToast.error(`Failed to load scripts: ${error}`)
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchScripts()
  }, [fetchScripts])

  // Handle script selection
  const handleSelectScript = (script: ScriptInfo) => {
    setSelectedScript(script)
    setParams({})
    setResult(null)
  }

  // Handle script execution
  const handleExecute = async () => {
    if (!selectedScript) return

    try {
      setIsExecuting(true)
      const response = await executeScript(selectedScript.name, {
        params,
        dry_run: dryRun
      })
      setResult(response)
      setShowResultModal(true)

      if (response.status === 'success') {
        BetterToast.success(response.message)
      } else if (response.status === 'dry_run') {
        BetterToast.info('Dry run completed - no changes were made')
      } else if (response.status === 'partial') {
        BetterToast.warning('Script completed with some errors')
      } else {
        BetterToast.error('Script execution failed')
      }
    } catch (error) {
      BetterToast.error(`Failed to execute script: ${error}`)
    } finally {
      setIsExecuting(false)
    }
  }

  return (
    <div className='space-y-4'>
      {/* Header */}
      <Card>
        <CardHeader>
          <div className='flex justify-between items-center w-full'>
            <h2 className='text-xl font-bold'>Script Runner</h2>
            <Button variant='outlined' onClick={fetchScripts} disabled={isLoading}>
              {isLoading ? <Spinner size='sm' /> : 'Refresh'}
            </Button>
          </div>
        </CardHeader>
        <CardBody>
          <p className='text-sm text-gray-600'>
            Run maintenance scripts and backfill operations. Always use dry-run mode first to
            preview changes before applying them.
          </p>
        </CardBody>
      </Card>

      <div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
        {/* Script List */}
        <Card className='md:col-span-1'>
          <CardHeader>
            <h3 className='text-lg font-semibold'>Available Scripts</h3>
          </CardHeader>
          <CardBody>
            {isLoading ? (
              <div className='flex justify-center py-4'>
                <Spinner />
              </div>
            ) : scripts.length === 0 ? (
              <p className='text-sm text-gray-500 text-center py-4'>No scripts available</p>
            ) : (
              <div className='space-y-2'>
                {scripts.map(script => (
                  <button
                    key={script.name}
                    className={`w-full text-left p-3 rounded-lg border transition-colors ${
                      selectedScript?.name === script.name
                        ? 'border-primary bg-primary-50'
                        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                    }`}
                    onClick={() => handleSelectScript(script)}
                    data-testid={`script-${script.name}`}
                  >
                    <div className='font-medium text-sm'>{script.name}</div>
                    <p className='text-xs text-gray-500 mt-1 line-clamp-2'>{script.description}</p>
                  </button>
                ))}
              </div>
            )}
          </CardBody>
        </Card>

        {/* Script Configuration */}
        <Card className='md:col-span-2'>
          <CardHeader>
            <h3 className='text-lg font-semibold'>
              {selectedScript ? selectedScript.name : 'Select a Script'}
            </h3>
          </CardHeader>
          <CardBody>
            {!selectedScript ? (
              <p className='text-sm text-gray-500 text-center py-8'>
                Select a script from the list to configure and run it.
              </p>
            ) : (
              <div className='space-y-6'>
                {/* Description */}
                <div>
                  <h4 className='text-sm font-semibold mb-1'>Description</h4>
                  <p className='text-sm text-gray-600'>{selectedScript.description}</p>
                </div>

                {/* Parameters */}
                <div>
                  <h4 className='text-sm font-semibold mb-2'>Parameters</h4>
                  <ScriptParamsForm
                    schema={selectedScript.param_schema}
                    params={params}
                    onChange={setParams}
                  />
                </div>

                {/* Dry Run Toggle */}
                <div className='flex items-center gap-3 p-3 bg-gray-50 rounded-lg'>
                  <Switch
                    isSelected={dryRun}
                    onValueChange={setDryRun}
                    color='primary'
                    data-testid='dry-run-toggle'
                  >
                    <span className='text-sm font-medium'>Dry Run Mode</span>
                  </Switch>
                  <p className='text-xs text-gray-500'>
                    {dryRun
                      ? 'Preview changes without applying them'
                      : '⚠️ Changes will be applied to the database'}
                  </p>
                </div>

                {/* Execute Button */}
                <div className='flex gap-2'>
                  <Button
                    variant={dryRun ? 'outlined' : 'primary'}
                    onClick={handleExecute}
                    disabled={isExecuting}
                    className='flex-1'
                    data-testid='execute-button'
                  >
                    {isExecuting ? (
                      <>
                        <Spinner size='sm' className='mr-2' />
                        Running...
                      </>
                    ) : dryRun ? (
                      'Preview Changes (Dry Run)'
                    ) : (
                      'Execute Script'
                    )}
                  </Button>
                  {result && (
                    <Button variant='outlined' onClick={() => setShowResultModal(true)}>
                      View Last Result
                    </Button>
                  )}
                </div>
              </div>
            )}
          </CardBody>
        </Card>
      </div>

      {/* Results Modal */}
      <Modal
        isOpen={showResultModal}
        onClose={() => setShowResultModal(false)}
        hideCloseButton={false}
        className='w-[900px]'
      >
        <ModalBody>
          <h3 className='text-lg font-semibold mb-4'>
            {result?.status === 'dry_run' ? 'Dry Run Results' : 'Execution Results'}
          </h3>
          {result && <ScriptResults result={result} />}
        </ModalBody>
        <ModalFooter>
          <Button variant='outlined' onClick={() => setShowResultModal(false)}>
            Close
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  )
}

export default ScriptRunner
